import { useEffect, useCallback } from 'react';
import { Container, Stack, Typography, Button, Grid } from '@mui/material';
import { Box } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
// Components
import 'react-datepicker/dist/react-datepicker.css';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useReceiveSelector from '../../store/Slice/Receive/ReceiveSelector';
import { getReceiveRecordData } from '../../store/Slice/Receive/ReceiveSlice';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import { fDate2 } from '../../utils/formatTime';
import {
  getBankBranchData,
  getBankDropdownData,
  getLoanTypeData,
  saveApiData
} from '../../store/Slice/Global/GlobalSlice';
import { ReceiveApi } from '../../utils/Apis';

function ViewFile() {
  const {
    handleSubmit: handleReduxSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onChange', // Add this to validate on blur
    defaultValues: {
      grossInvoiceValue: '',
      deduction: '',
      ownerName: ''
    }
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { rowId } = location?.state || {};
  const { LoginData } = useLoginSelector();
  const { ReceiveRecordData } = useReceiveSelector();
  const { LoanTypeDrop, BankBranchDrop, BankDrop } = useGlobalSelector();

  const matchStatusOptions = [
    { label: 'Matched', value: true },
    { label: 'Not Matched', value: false }
  ];

  const getReceiveRecordApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      filterModel: {
        bankId: rowId?.bankId || null,
        branchId: rowId?.branchId || null,
        loanTypeId: rowId?.loanTypeId || null,
        misIsMatch: rowId?.misIsMatch || null,
        fromDate: null,
        toDate: null,
        fileNumber: rowId?.fileNumber || '',
        receivableId: rowId?.receivableId || ''
      }
    };
    dispatch(getReceiveRecordData(credentials));
  }, [rowId, LoginData]);

  useEffect(() => {
    getReceiveRecordApi();
  }, [getReceiveRecordApi]);

  useEffect(() => {
    if (ReceiveRecordData) {
      const myData = ReceiveRecordData?.[0];
      setValue('bankId', myData?.bankId);
      setValue('borrowerName', myData?.borrowerName);
      setValue('branchId', myData?.branchId);
      setValue('branchName', myData?.branchName);
      setValue('empClass', myData?.empClass);
      const rawDisbursed = myData?.fileDisbursedDate;
      const safeDisbursed =
        rawDisbursed && String(rawDisbursed).toLowerCase() !== 'null' ? fDate2(rawDisbursed) : ''; // or null if you prefer
      setValue('fileDisbursedDate', safeDisbursed);
      setValue('fileNumber', myData?.fileNumber);
      setValue('invoiceNo', myData?.invoiceNo);
      setValue('loanAmountDisbursed', myData?.loanAmountDisbursed);
      setValue('loanTypeId', myData?.loanTypeId);
      setValue('misIsMatch', myData?.misIsMatch);
      setValue('misMonth', myData?.misMonth);
      setValue('receivableDeduction', myData?.receivableDeduction);
      setValue('receivableId', myData?.receivableId);
      setValue('revenueId', myData?.revenueId);

      // second card
      setValue('netInvoiceValue', myData?.receivableInvoice?.netInvoiceValue);
      setValue('grossInvoiceValue', myData?.receivableInvoice?.grossInvoiceValue);
      setValue('deduction', myData?.receivableInvoice?.deduction);
      setValue('adjustedInvoiceValue', myData?.receivableInvoice?.adjustedInvoiceValue);
      setValue('totalGst', myData?.receivableInvoice?.totalGst || 18);

      setValue('receivableInvoiceId', myData?.receivableInvoicePayments?.receivableInvoiceId);

      // Nested object fields
      setValue('ownerName', myData?.receivableInvoicePayments?.ownerName);
      setValue('commissionDeduction', myData?.receivableInvoicePayments?.commissionDeduction);
      setValue(
        'commissionNetPayableAmount',
        myData?.receivableInvoicePayments?.commissionNetPayableAmount
      );
      setValue(
        'receivableInvoicePaymentId',
        myData?.receivableInvoicePayments?.receivableInvoicePaymentId
      );
    }
  }, [ReceiveRecordData]);

  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,

        bankId: rowId?.bankId || null,
        fileNumber: rowId?.fileNumber || '',
        receivableId: rowId?.receivableId || '',
        receivableDeduction: {
          receivableDeductionId: null,
          receivableId: null,
          totalProcessingFee: null,
          bankWaiver: null,
          applicantShare: null,
          companyShare: null,
          processfeeStatusId: null,
          feeDeductedFromInvoice: null
        },
        receivableInvoice: {
          receivableInvoiceId: ReceiveRecordData?.receivableInvoice?.receivableInvoiceId,
          invoiceNumber: ReceiveRecordData?.receivableInvoice?.invoiceNumber,
          grossInvoiceValue: data?.grossInvoiceValue,
          deduction: data?.deduction,
          adjustedInvoiceValue: ReceiveRecordData?.receivableInvoice?.adjustedInvoiceValue,
          totalGst: ReceiveRecordData?.receivableInvoice?.totalGst || 18,
          netInvoiceValue: ReceiveRecordData?.receivableInvoice?.netInvoiceValue
        },
        receivableInvoicePayments: {
          receivableInvoicePaymentId:
            ReceiveRecordData?.receivableInvoice?.receivableInvoicePaymentId,
          commissionNetPayableAmount:
            ReceiveRecordData?.receivableInvoice?.commissionNetPayableAmount,
          commissionDeduction: data?.commissionDeduction,
          ownerName: data?.ownerName
        }
      };

      try {
        const res = await dispatch(saveApiData(ReceiveApi?.receiveSave, finalData));
        if (res?.status === '0') navigate('/receivable');
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [LoginData] // 🔹 depends on LoginData
  );

  // loan type & Bank Dropdown
  const getLoanTypeBankDropAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(data));
    dispatch(getBankDropdownData(data));
  }, [LoginData]);

  const handleBranchDetails = useCallback(() => {
    const myData = ReceiveRecordData?.[0]?.bankId;
    if (!myData) return '';
    dispatch(getBankBranchData(myData));
  }, [ReceiveRecordData]);

  useEffect(() => {
    handleBranchDetails();
  }, [handleBranchDetails]);

  useEffect(() => {
    getLoanTypeBankDropAPi();
  }, [getLoanTypeBankDropAPi]);

  return (
    <Page title="Partner Management System || Receivable">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="View File" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Scrollbar>
            <form id="addPaybaleFile" onSubmit={handleReduxSubmit(onSubmit)}>
              <Typography variant="body1" className="headingLineUp headingLineUpOut">
                File Details
              </Typography>
              <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={4}>
                    <CustomSelect
                      name="bankId"
                      label="Bank Name"
                      control={control}
                      errors={errors}
                      options={BankDrop || []}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="fileNumber"
                      label="File No"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="borrowerName"
                      label="Borrower Name"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomSelect
                      name="branchId"
                      label="Branch Name"
                      control={control}
                      errors={errors}
                      options={BankBranchDrop || []}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="empClass"
                      label="Employee Class"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="fileDisbursedDate"
                      label="Disbured Date"
                      type="date"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="loanAmountDisbursed"
                      label="Disbursed Amount"
                      type="Number"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomSelect
                      name="loanTypeId"
                      label="Loan Product Type"
                      control={control}
                      errors={errors}
                      options={LoanTypeDrop || []}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="misMonth"
                      label="MIS Month"
                      type="month"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomSelect
                      name="misIsMatch"
                      label="MIS Status"
                      control={control}
                      errors={errors}
                      options={matchStatusOptions || []}
                      disabled
                    />
                  </Grid>
                </Grid>{' '}
              </Box>
              <Typography variant="body1" className="headingLineUp headingLineUpOut">
                Invoice Calculation
              </Typography>
              <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={4}>
                    <CustomInput
                      name="grossInvoiceValue"
                      label="Gross Invoice Value"
                      type="Number"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldType="AMOUNT_FIELD"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="deduction"
                      label="Deduction"
                      type="Number"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldType="AMOUNT_FIELD"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="adjustedInvoiceValue"
                      label="Adjusted Invoice Value"
                      type="Number"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="totalGst"
                      label="GST %"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="netInvoiceValue"
                      label="Net Invoice Value"
                      type="Number"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                </Grid>{' '}
              </Box>
              <Typography variant="body1" className="headingLineUp headingLineUpOut">
                Partner Payout Details
              </Typography>
              <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={4}>
                    <CustomInput
                      name="ownerName"
                      label="Partner Name"
                      control={control}
                      errors={errors}
                      isRequired
                      fieldCode="NAME"
                      fieldType="ALPHABET"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="commissionDeduction"
                      label="Applicable Commission %"
                      type="Number"
                      control={control}
                      errors={errors}
                      disabled={
                        ReceiveRecordData?.receivableInvoice?.adjustedInvoiceValue === 'null'
                      }
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomInput
                      name="commissionNetPayableAmount"
                      label="Payout Amount"
                      type="Number"
                      control={control}
                      errors={errors}
                      disabled
                    />
                  </Grid>
                </Grid>{' '}
              </Box>
            </form>{' '}
            <Box className="" textAlign="right">
              <Button type="submit" form="addPaybaleFile" variant="contained" className="mainBtn">
                Submit
              </Button>
            </Box>
          </Scrollbar>
        </Box>
      </Container>
    </Page>
  );
}
export default ViewFile;
